/**
 * buildResultParams
 *
 * Pure, dependency-free helper that assembles the query parameters for the
 * `GET /api/result` request. Extracted from `testLogsApi.fetchLogs` so the
 * single-day vs. range param encoding can be unit/property tested without
 * mocking HTTP.
 *
 * Behavior (must match the existing param assembly in testLogsApi.js):
 * - Always includes `date`, `page`, `size`, `localTimeZone`, `serverTimeZone`.
 * - Includes `type` ONLY when `type` is truthy and not `'ALL'` (the backend
 *   filters when a concrete type is provided).
 * - Includes `endDate` ONLY when it is a non-empty string that differs from
 *   `date` (a true multi-day range, per design decision DD1). Single-day
 *   selections — an empty `endDate` or an `endDate` equal to `date` — OMIT the
 *   `endDate` parameter, preserving the existing wire format for the common
 *   case and letting the backend default `endDate := date`.
 *
 * @param {Object} args
 * @param {string} args.date - Start (or single) date in YYYY-MM-DD format.
 * @param {string} [args.endDate] - Optional end date in YYYY-MM-DD format ('' or undefined for single-day).
 * @param {number} args.page - Page number (0-indexed).
 * @param {number} args.size - Number of items per page.
 * @param {string} args.localTimeZone - Local time zone (e.g. 'America/New_York').
 * @param {string} args.serverTimeZone - Server time zone (e.g. 'UTC').
 * @param {string} [args.type='ALL'] - Optional type filter ('SINGLE_API', 'ORCHESTRATION', 'LOAD_TEST', 'ADVANCED_ORCHESTRATION', or 'ALL').
 * @returns {Object} The assembled `/api/result` query params.
 *
 * @example
 * buildResultParams({ date: '2026-01-09', endDate: '', page: 0, size: 20, localTimeZone: 'UTC', serverTimeZone: 'UTC', type: 'ALL' });
 * // => { date: '2026-01-09', page: 0, size: 20, localTimeZone: 'UTC', serverTimeZone: 'UTC' }
 *
 * @example
 * buildResultParams({ date: '2026-01-01', endDate: '2026-01-05', page: 0, size: 20, localTimeZone: 'UTC', serverTimeZone: 'UTC', type: 'SINGLE_API' });
 * // => { date: '2026-01-01', page: 0, size: 20, localTimeZone: 'UTC', serverTimeZone: 'UTC', type: 'SINGLE_API', endDate: '2026-01-05' }
 */
export const buildResultParams = ({
  date,
  endDate,
  page,
  size,
  localTimeZone,
  serverTimeZone,
  type = 'ALL',
}) => {
  const params = {
    date,
    page,
    size,
    localTimeZone,
    serverTimeZone,
  };

  // Only add type param if it's not 'ALL' (backend filters when type is provided).
  if (type && type !== 'ALL') {
    params.type = type;
  }

  // Only add endDate for a true multi-day range: a non-empty end date that
  // differs from the start date (DD1). Single-day selections omit endDate.
  if (typeof endDate === 'string' && endDate !== '' && endDate !== date) {
    params.endDate = endDate;
  }

  return params;
};

export default buildResultParams;
